import React from "react";
import { useEffect, useState } from "react";
import { getRecruiterProfilePictureURL, validatePasswordRules } from "../../helpers/Common";
import { ReactComponent as CameraSvg } from '../../assets/svg/camera.svg';
import { ReactComponent as DropDown } from '../../assets/svg/drop_down.svg';
import { TextInput } from "../Shared/Inputs/TextInput";
import { RecruiterProfileModel, RecruiterProfileValidation, RecruiterPasswordModel, RecruiterPasswordValidation } from "../../models/RecruiterAccountModel";
import { Button } from "../Shared/Buttons/Button";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { validateEmail, validateName, validateImageFile } from "../../helpers/Common";
import { useAlert } from "../../hooks/useAlert";
import { postRecruiterProfile, getRecruitrProfile, updateRecruiterPassword, addProfilePicture } from "../../services/RecruiterService";
import { ResponseModel } from "../../models/ResponseModel";
import useErrorPage from "../../hooks/useErrorPage";
import { LoggedRecruiter } from '../../models/LoggedRecruiter';
import { RecruiterProfileLoader } from "../Loaders/RecruiterProfileLoader";
import { RecruiterPasswordLoader } from "../Loaders/RecruiterPasswordLoader";
import { UserType } from "../../enums/UserType";
import { PasswordValidation } from "../Shared/PasswordValidationPanel";

interface Props {
    styles: any;
    recruiter: LoggedRecruiter;
}

const initialProfileValidation: RecruiterProfileValidation = {
    forename: {},
    surname: {},          
    email: {},
    secondaryEmail: {}
}

const initialPasswordValidation: RecruiterPasswordValidation = {
    currentPassword: {},
    newPassword: {},          
    confirmNewPassword: {}
}

export const Account: React.FC<Props> = (props) => {
    const recruiter = props.recruiter;
    const styles = props.styles;
    const [ProfilePictureloading, setProfilePictureLoading] = useState(false);
    const recruiterProfilePictureURL = getRecruiterProfilePictureURL(recruiter.recruiterId);
    const [uploadedProfilePicture, setUploadedProfilePicture] = useState<File>();
    const [profilePictureURL, setProfilePictureURL] = useState<string>(recruiterProfilePictureURL);
    const [profileLoading, setProfileLoading] = useState<boolean>(true);
    const [passwordLoading, setPasswordLoading] = useState<boolean>(false);

    const  intialPasswordModel: RecruiterPasswordModel = {
        recruiterId: recruiter.recruiterId,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: ""
    }

    const intialProfileModel: RecruiterProfileModel = {
        recruiterId: recruiter.recruiterId,
        organizationId: recruiter.organizationId,
        forename: '',
        surname: '',
        jobTitle: '',
        email: '',
        secondaryEmail: ''
    }

    const [recruiterProfile, setRecruiterProfile] = useState<RecruiterProfileModel>(intialProfileModel);
    const [profileValidation, setProfileValidation] = useState<RecruiterProfileValidation>(initialProfileValidation);
    const [recruiterPassword, setRecruiterPassword] = useState<RecruiterPasswordModel>(intialPasswordModel);
    const [passwordValidation, setPasswordValidation] = useState<RecruiterPasswordValidation>(initialPasswordValidation);
    const [passwordFocused, setPasswordFocused] = useState<boolean>(false);
    const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
    const { Alert, openAlert } = useAlert();
    const [toErrorPage] = useErrorPage();

    useEffect(() => {
        getProfileData();
    }, []);

    async function getProfileData() {
        await getRecruitrProfile(recruiter.recruiterId, recruiter.token)
        .then((profileInformation: RecruiterProfileModel) => {
            setRecruiterProfile(profileInformation);
            setProfileLoading(false);           
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);
        });
    }
    
    const handlePasswordInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setRecruiterPassword((values: any) => ({ ...values, [name]: value }));
        if(passwordValidation[name] && passwordValidation[name]!.invalid) {
            validateInput(name, value);
        }
        if (name === "newPassword" && recruiterPassword.confirmNewPassword !== "") {
            if (value !== recruiterPassword.confirmNewPassword) {
                setPasswordValidation((v: any) => ({
                    ...v,
                    confirmNewPassword: { valid: false, invalid: true, invalidMessage: "New Password and confirmation do not match!" }
                }));
            } else {
                setPasswordValidation((v: any) => ({
                    ...v,
                    confirmNewPassword: { valid: true, invalid: false }
                }));
            }
        }
    }

    const handleProfileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setRecruiterProfile((values: any) => ({ ...values, [name]: value }));
        if(profileValidation[name] && profileValidation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);
    }

    const validateInput = (name: string, value: string) => {      
        if(name === "forename" || name === "surname") {
            setProfileValidation((values: any) => ({...values, [name]: validateName(name, value, 200)}));            
        }
    
        if(name === "email" || name === "secondaryEmail") {
            if(value === "") {
                if(name === "email") {
                    setProfileValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Email is required"}}));
                }
                else {
                    setProfileValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
                }
            } 
            else if (!validateEmail(value)) {
                setProfileValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
            } 
            else {
                setProfileValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }

        if(name === "currentPassword") {
            if(value === "") {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password is required!"}}));
            } 
            else {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }

        if(name === "newPassword") {
            if(value === "") {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "New Password is required!"}}));
            } 
            else if(!validatePasswordRules(value).isValid) {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"}}));
            }
            else {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }

        if(name === "confirmNewPassword") {
            const newPassword = recruiterPassword.newPassword;      
            if(value === "") {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password confirmation is required!"}}));
            } 
            else if (newPassword !== value) {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "New Password and confirmation do not match!"}}));
            } 
            else {
                setPasswordValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }  
    }

    const validateProfileForm = () => {
        let formValidation: RecruiterProfileValidation = {
            forename: {valid: true},
            surname: {valid: true},
            email: {valid: true},
            secondaryEmail: {valid: true}
        }
        formValidation.forename = validateName("forename", recruiterProfile.forename, 200);        
        formValidation.surname = validateName("surname", recruiterProfile.surname, 200);

        if(recruiterProfile.email === "") {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Email is required!"};
        } 
        else if(!validateEmail(recruiterProfile.email)) {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        }
        
        if(recruiterProfile.secondaryEmail && !validateEmail(recruiterProfile.secondaryEmail)) {
            formValidation.secondaryEmail = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        } 
        setProfileValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key]!.valid).length;
        return validInputCount;
    }

    const validatePasswordForm = () => {
        let passwordFormValidation: RecruiterPasswordValidation = {
            currentPassword: {valid: true},
            newPassword: {valid: true},
            confirmNewPassword: {valid: true}
        }
        if(recruiterPassword.currentPassword === "") {
            passwordFormValidation.currentPassword = {valid: false, invalid: true, invalidMessage: "Current Password is required!"};
        }
        if(recruiterPassword.newPassword === "") {
            passwordFormValidation.newPassword = {valid: false, invalid: true, invalidMessage: "New Password is required!"};
        }
        else if(!validatePasswordRules(recruiterPassword.newPassword).isValid) {
            passwordFormValidation.newPassword = {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"};
        }
        if(recruiterPassword.confirmNewPassword === "") {    
            passwordFormValidation.confirmNewPassword = {valid: false, invalid: true, invalidMessage: "Confimation Password is required!"};
        }
        else if(recruiterPassword.newPassword !== recruiterPassword.confirmNewPassword) {
            passwordFormValidation.confirmNewPassword = {valid: false, invalid: true, invalidMessage: "New Password and confirmation do not match!"};
        }
        setPasswordValidation(passwordFormValidation);
        let validInputCount = Object.keys(passwordFormValidation).filter(key => passwordFormValidation[key]!.valid).length;
        return validInputCount;
    }

    const expandPasswordForm = () => {
        setShowPasswordForm(!showPasswordForm);
    }

    const openProfilePictureFileSelect = () => {
        document.getElementById("profile-picture")?.click();
    }

    const handleProfilePictureFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let selectedFiles = event.currentTarget?.files;
        if(selectedFiles && selectedFiles.length > 0){
            let validation = validateImageFile(selectedFiles[0]);
            if(validation.valid){
                let pictureURL = URL.createObjectURL(selectedFiles[0]);
                setUploadedProfilePicture(selectedFiles[0]);
                setProfilePictureURL(pictureURL);
            }
            else{
                openAlert("error", validation.invalidMessage!)
            }
            event.target.value = "";
        }
    }

    async function handleAddProfilePicture() {
        setProfilePictureLoading(true);
        let formData = new FormData();
        formData.append("recruiterId", recruiter.recruiterId.toString()!);
        formData.append("profilePicture", uploadedProfilePicture!);
        await addProfilePicture(formData, recruiter.token)
        .then((response: ResponseModel) => {
            openAlert(response.status ? "success" : "error", response.message);
            window.location.reload();
        })
        .catch(() => {
            openAlert("error", "There was an error during the process!")
            setProfilePictureURL(recruiterProfilePictureURL);
        })
        setProfilePictureLoading(false);
        setUploadedProfilePicture(undefined);
    }

    const removeSelectedProfilePicture = () => {
        setUploadedProfilePicture(undefined);
        setProfilePictureURL(recruiterProfilePictureURL);
    }

    async function handlePasswordFormSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(passwordValidation).filter(key => passwordValidation[key]!.valid).length;
        if(validInputCount < 3){
            validInputCount = validatePasswordForm();
        }
        if(validInputCount == 3) { 
            setPasswordLoading(true);
            await updateRecruiterPassword(recruiterPassword, recruiter.token)
            .then((response: ResponseModel) => {
                if (response.status) {
                    openAlert("success", response.message);
                    setRecruiterPassword(intialPasswordModel);
                }
                else {
                    openAlert("error", response.message);
                    setPasswordValidation((values: any) => ({
                        ...values, 
                        ["currentPassword"]: { valid: false, invalid: true, invalidMessage: response.message }
                    }));
                } 
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            })
            .finally(()=> {
                setPasswordLoading(false);
            });
        }
        else {
            openAlert("error", "Some mandatory fields are invalid.");
        }     
    }

    async function handleProfileFormSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(profileValidation).filter(key => profileValidation[key]!.valid).length;
        if(validInputCount < 4) {
            validInputCount = validateProfileForm();
        }
        if(validInputCount === 4) { 
            setProfileLoading(true);
            await postRecruiterProfile(recruiterProfile, recruiter.token)
            .then((response: ResponseModel) => {
                if (response.status) {
                    openAlert("success", response.message);
                    setRecruiterProfile(response.data.recruiter);
                }
                else {
                    openAlert("error", response.message);
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            })
            .finally(()=> {
                setProfileLoading(false); 
            });
        }
        else {
            openAlert("error", "Some mandatory fields are invalid.");            
        }          
    }

    return(
        <>   
            <div style={styles.mainWrapper}>
                <div style={styles.profile}>
                    <span style={styles.profileName}>{"Your Profile Picture"}</span>
                    <img style={styles.profilePhoto} src={profilePictureURL}/>
                    <Button style={styles.logo} onClick={openProfilePictureFileSelect}>
                        <CameraSvg style={styles.cameraIcon}/>
                    </Button>
                    <input type="file" name="profile-picture" id="profile-picture" onChange={handleProfilePictureFileChange} style={styles.fileInput}/>
                </div>
                <div style={styles.formGruop}>
                    {profileLoading ? <RecruiterProfileLoader/> : 
                    <form style={styles.form} onSubmit={handleProfileFormSubmit}> 
                        <div style={styles.inputGroup}>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"Forename"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"forename"}
                                    value={recruiterProfile?.forename}
                                    styles={styles.textGroup}
                                    handleChange={handleProfileInputChange}
                                    handleBlur={handleInputBlur}                           
                                    validation={profileValidation.forename}    
                                />
                            </div>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"Surname"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"surname"}
                                    value={recruiterProfile?.surname}
                                    styles={styles.textGroup}
                                    handleChange={handleProfileInputChange}
                                    handleBlur={handleInputBlur}                           
                                    validation={profileValidation.surname}    
                                /> 
                            </div>   
                        </div>
                        <hr style={styles.line}/>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Email"}</label>
                            <TextInput
                                inputType={"email"}
                                name={"email"}
                                value={recruiterProfile?.email}
                                styles={styles.textInput}
                                handleChange={handleProfileInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={profileValidation.email}    
                            /> 
                        </div> 
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Secondary Email"}</label>
                            <TextInput
                                inputType={"email"}
                                name={"secondaryEmail"}
                                value={recruiterProfile?.secondaryEmail}
                                styles={styles.textInput}
                                handleChange={handleProfileInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={profileValidation.secondaryEmail}    
                            /> 
                        </div>
                    </form> }
                    <hr style={styles.line}/>
                    <div style={styles.subTopic} onClick={expandPasswordForm}>Change Password <DropDown style={{...styles.droupDownIcon, ...(showPasswordForm && styles.droupDownIcon.active)}}/></div> 
                    {showPasswordForm && (passwordLoading ? <RecruiterPasswordLoader/> :
                    <form style={styles.updatePassword} onSubmit={handlePasswordFormSubmit}>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Current Password"}</label>
                            <TextInput
                                inputType={"password"}
                                name={"currentPassword"}
                                value={recruiterPassword?.currentPassword}
                                styles={styles.textInput}
                                handleChange={handlePasswordInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={passwordValidation.currentPassword}    
                            /> 
                        </div>
                        <hr style={styles.line}/>
                        <div
                            style={styles.inputWrapper}
                            onFocus={() => setPasswordFocused(true)}
                            onBlur={(e) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) setPasswordFocused(false);
                            }}
                        >
                            <label style={styles.label}>{"New Password"}</label>
                            <TextInput
                                inputType={"password"}
                                name={"newPassword"}
                                value={recruiterPassword?.newPassword}
                                styles={styles.textInput}
                                handleChange={handlePasswordInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={passwordValidation.newPassword}    
                            /> 
                            <PasswordValidation
                                password={recruiterPassword.newPassword}
                                touched={passwordValidation.newPassword?.invalid}
                                showStrength
                                focused={passwordFocused}
                            /> 
                        </div>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Confirm New Password"}</label>
                            <TextInput
                                inputType={"password"}
                                name={"confirmNewPassword"}
                                value={recruiterPassword?.confirmNewPassword}
                                styles={styles.textInput}
                                handleChange={handlePasswordInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={passwordValidation.confirmNewPassword}    
                            /> 
                        </div>
                        <Button type="submit" style={styles.updateButton}>{"UPDATE PASSWORD"}</Button>
                    </form>)}
                    <hr style={styles.line}/>
                    <Button style={styles.saveButton} disabled={profileLoading} onClick={handleProfileFormSubmit}>{"SAVE CHANGES"}</Button>   
                </div> 
            </div>
            <Alert/>
            <PopUpWindow open={uploadedProfilePicture != undefined} styles={styles.uploadImagePopup} toggle={removeSelectedProfilePicture}>
                <img style={styles.uploadedImage} src={profilePictureURL} alt="Profile Picture" />
                <Button style={styles.uploadImageButton} disabled={ProfilePictureloading} onClick={handleAddProfilePicture}>Add Profile Picture</Button>
            </PopUpWindow>
        </>      
    )
}